<template>
  <div class="bg-u-px-xxl">
    <bg-text size="heading-4">Ubah Data Penyewa</bg-text>

    <validation-observer v-slot="{ invalid }">
      <div class="bg-u-mt-xxl flex gap-xl">
        <bg-field label="Nama" class="flex-1 required-field">
          <bg-skeleton v-if="isFetchingTenantContact" width="100%" />

          <bg-input v-else v-model="tenantName" data-testid="input-name" />
        </bg-field>

        <validation-provider
          name="Nomor HP"
          rules="phone_format|phone_numeric|phone_min:8|phone_max:14"
          class="flex-1"
          v-slot="{ errors }"
        >
          <bg-field
            label="Nomor HP"
            class="required-field"
            :error="errors.length > 0 || !isPhoneNumberValid"
            :message="getPhoneNumberFieldMessage(errors)"
          >
            <bg-skeleton v-if="isFetchingTenantContact" width="100%" />

            <bg-input
              v-else
              v-model="tenantPhoneNumber"
              data-testid="input-phone-number"
              type="tel"
              @keyup="validatePhoneNumber($event.target.value)"
            />
          </bg-field>
        </validation-provider>
      </div>

      <div class="bg-u-mt-md">
        <bg-field
          label="Lampiran Konfirmasi Perubahan Data"
          class="required-field"
        >
          <div v-if="isFetchingTenantContact" class="flex gap-md bg-u-mt-md">
            <bg-skeleton v-for="i in 2" :key="i" width="275px" height="156px" />
          </div>

          <div v-else class="flex gap-md bg-u-mt-md">
            <bg-image-uploader
              trigger-title="Upload di sini"
              trigger-icon-name="camera"
              :box-width="275"
              accept="image/*"
              :progress="100"
              @upload="uploadImage(...arguments, 'main')"
              @delete="deleteImage('main')"
            />

            <bg-image-uploader
              trigger-title="Upload di sini"
              trigger-icon-name="camera"
              :box-width="275"
              accept="image/*"
              :progress="100"
              @upload="uploadImage(...arguments, 'additional')"
              @delete="deleteImage('additional')"
            />
          </div>
        </bg-field>
      </div>

      <bg-button
        variant="primary"
        size="lg"
        :disabled="!isFormValid || invalid"
        class="bg-u-mt-xxxl"
        @click="showConfirmationModal"
      >
        Simpan
      </bg-button>
    </validation-observer>

    <bg-modal
      v-model="isConfirmationModalOpen"
      title="Yakin ingin ubah data penyewa?"
      description="Perubahan ini akan langsung memperbarui data penyewa
			pada informasi kontrak. Pastikan data baru sudah benar dan telah
			dikonfirmasi ke penyewa."
      :close-on-click-backdrop="false"
      button-main-text="Ya, Ubah"
      :button-main-props="{ loading: isSubmitting }"
      data-testid="confirmation-modal"
      @close="closeConfirmationModal"
      @click-main-action="submitForm"
    >
      <div class="bg-u-bg-magenta-100 bg-u-p-md bg-u-mt-md bg-u-radius-lg">
        <div class="flex justify-space-between">
          <bg-text size="body-4">Nama:</bg-text>
          <bg-text size="body-3">{{ tenantName }}</bg-text>
        </div>

        <div class="flex justify-space-between bg-u-mt-xs">
          <bg-text size="body-4">Nomor HP:</bg-text>
          <bg-text size="body-3">
            {{ tenantPhoneNumber }}
          </bg-text>
        </div>
      </div>
    </bg-modal>

    <bg-modal
      v-model="isLeaveConfirmationModalOpen"
      title="Apakah Anda yakin ingin tinggalkan halaman ini?"
      description="Perubahan yang sudah dibuat pada halaman ini akan
			hilang dan tidak tersimpan."
      button-main-text="Batal"
      button-second-text="Ya, Keluar"
      :button-main-props="{ variant: 'secondary' }"
      :button-second-props="{ variant: 'primary' }"
      data-testid="leave-confirmation-modal"
      @click-main-action="closeLeaveConfirmationModal"
      @click-second-action="confirmLeave"
      @close="closeLeaveConfirmationModal"
    />
  </div>
</template>

<script>
import { getCurrentInstance, computed, ref, watch } from '@vue/composition-api';

import {
  BgText,
  BgField,
  BgInput,
  BgImageUploader,
  BgButton,
  BgModal,
  BgSkeleton,
} from 'bangul-vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import _debounce from 'lodash/debounce';

import MamipayTenantApi from '@admin_endpoints/mamipay/tenant';
import '@/utils/validation-rules';

import tenantApi from '@admin/api/endpoints/mamipay/tenant';
import mamipayMediaApi from '@admin/api/endpoints/mamipay/media';

export default {
  name: 'RoomAllotmentTenantContactEdit',

  components: {
    BgText,
    BgField,
    BgInput,
    BgImageUploader,
    BgButton,
    BgModal,
    BgSkeleton,
    ValidationObserver,
    ValidationProvider,
  },

  created() {
    this.fetchTenantContact();
  },

  beforeRouteLeave(to, _, next) {
    const isLogout = to.name === 'login';

    if (isLogout || this.isLeavingAfterSubmit || this.pendingNavigation) {
      this.pendingNavigation = null;
      next();
    } else {
      this.pendingNavigation = to.fullPath;
      this.showLeaveConfirmationModal();
      next(false);
    }
  },

  setup() {
    const instance = getCurrentInstance();
    const { $route, $router, $toast, $error } = instance.proxy;

    const isFetchingTenantContact = ref(false);

    const tenantId = ref(null);
    const tenantName = ref('');
    const tenantPhoneNumber = ref('');
    const attachmentImage = ref({
      main: null,
      additional: null,
    });

    const contractId = computed(() => $route.params.contractId);

    const fetchTenantContact = async () => {
      try {
        isFetchingTenantContact.value = true;

        const response = await tenantApi.getTenantInfo(contractId.value);

        if (response && response.data) {
          const { tenant_info: tenantInfo } = response.data;

          tenantId.value = tenantInfo.id;
          tenantName.value = tenantInfo.name;
          tenantPhoneNumber.value = tenantInfo.phone_number;
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || 'Terjadi galat, silakan coba lagi.';

        $toast.show(errorMessage);
        $error.report(error);
      } finally {
        isFetchingTenantContact.value = false;
      }
    };

    const payload = computed(() => ({
      tenant_id: tenantId.value,
      name: tenantName.value,
      phone_number: tenantPhoneNumber.value,
    }));

    const uploadImage = (file, type = 'main') => {
      attachmentImage.value[type] = file;
    };

    const deleteImage = (type = 'main') => {
      attachmentImage.value[type] = null;
    };

    const usePhoneNumberValidation = phoneNumber => {
      const isPhoneNumberValidating = ref(false);
      const isPhoneNumberValid = ref(true);
      const phoneNumberValidationMessage = ref('');
      const validationRequestId = ref(0);

      const PHONE_VALIDATION_DELAY = 500;

      function resetValidationState() {
        isPhoneNumberValidating.value = false;
        isPhoneNumberValid.value = true;
        phoneNumberValidationMessage.value = '';
      }

      // Phone number formatting watcher
      watch(
        phoneNumber,
        (phoneNumberValue, oldValue) => {
          // Prevent infinite loop
          if (phoneNumberValue === oldValue) return;

          let phoneNumberDigitsOnly = phoneNumberValue.replace(/\D+/g, '');

          if (phoneNumberDigitsOnly.startsWith('628')) {
            phoneNumberDigitsOnly = phoneNumberDigitsOnly.replace('628', '08');
          }

          if (phoneNumberDigitsOnly !== phoneNumberValue) {
            phoneNumber.value = phoneNumberDigitsOnly;
          }
        },
        { immediate: true }
      );

      const validatePhoneNumber = _debounce(async phoneNumberValue => {
        if (
          !phoneNumberValue ||
          phoneNumberValue.length < 8 ||
          phoneNumberValue.length > 14
        ) {
          resetValidationState();
          return;
        }

        if (!/^08/.test(phoneNumberValue)) {
          resetValidationState();
          return;
        }

        // Generate unique request ID to prevent race conditions
        validationRequestId.value += 1;

        const currentRequestId = validationRequestId.value;

        isPhoneNumberValidating.value = true;

        try {
          const response = await MamipayTenantApi.validateTenantPhoneNumber(
            contractId.value,
            phoneNumberValue
          );

          // Only process response if this is still the latest request
          if (currentRequestId === validationRequestId.value) {
            const { is_valid: isValid, message } = response.data;

            isPhoneNumberValid.value = isValid;
            isPhoneNumberValidating.value = false;

            phoneNumberValidationMessage.value = !isValid ? message : '';
          }
        } catch (error) {
          if (currentRequestId === validationRequestId.value) {
            const errorMessage =
              error?.response?.data?.error ||
              'Terjadi galat, silakan coba lagi.';

            $toast.show(errorMessage);
          }

          isPhoneNumberValidating.value = false;
        }
      }, PHONE_VALIDATION_DELAY);

      const getPhoneNumberFieldMessage = errors => {
        if (errors.length > 0) {
          return errors[0];
        }

        if (isPhoneNumberValidating.value) {
          return 'Mohon menunggu, sedang memuat data';
        }

        return phoneNumberValidationMessage.value;
      };

      return {
        isPhoneNumberValidating,
        isPhoneNumberValid,
        phoneNumberValidationMessage,
        validatePhoneNumber,
        getPhoneNumberFieldMessage,
      };
    };

    const {
      isPhoneNumberValidating,
      isPhoneNumberValid,
      phoneNumberValidationMessage,
      validatePhoneNumber,
      getPhoneNumberFieldMessage,
    } = usePhoneNumberValidation(tenantPhoneNumber);

    const isFormValid = computed(
      () =>
        tenantName.value &&
        tenantPhoneNumber.value &&
        attachmentImage.value.main &&
        isPhoneNumberValid.value
    );

    const useSubmit = () => {
      const isConfirmationModalOpen = ref(false);
      const isSubmitting = ref(false);
      const isLeavingAfterSubmit = ref(false);

      const showConfirmationModal = () => {
        isConfirmationModalOpen.value = true;
      };

      const closeConfirmationModal = () => {
        isConfirmationModalOpen.value = false;
      };

      const uploadAttachment = async (type = 'main') => {
        try {
          const formData = new FormData();

          formData.append('media', attachmentImage.value[type]);

          const params = {
            type: 'tenant_phone_number',
            user_id: tenantId.value,
          };

          const query = new URLSearchParams(params);

          const res = await mamipayMediaApi.postUploadMedia(formData, query);

          const { media } = res?.data || {};

          if (media.id) {
            return media.id;
          }
        } catch (error) {
          const errorMessage = 'Terjadi galat, silakan coba lagi.';

          $toast.show(errorMessage);
          $error.report(error);
        }

        return null;
      };

      const submitForm = async () => {
        if (!isFormValid.value) {
          return;
        }

        try {
          isSubmitting.value = true;

          const media = [];
          const mainAttachmentId = await uploadAttachment('main');

          if (mainAttachmentId) {
            media.push({ id: mainAttachmentId });
          } else {
            return;
          }

          if (attachmentImage.value.additional) {
            const additionalAttachmentId = await uploadAttachment('additional');

            if (additionalAttachmentId) {
              media.push({ id: additionalAttachmentId });
            } else {
              return;
            }
          }

          const response = await tenantApi.updateTenantInfo(contractId.value, {
            ...payload.value,
            media,
          });

          if (response && response.data?.tenant_info) {
            isLeavingAfterSubmit.value = true;

            $toast.show('Data berhasil diubah.');

            $router.replace({
              name: 'room-allotment-contract-detail',
            });
          } else {
            const error = response?.data?.error || 'Terjadi galat, silakan coba lagi.';

            $toast.show(error);
          }
        } catch (error) {
          const errorMessage =
            error?.response?.data?.error || 'Terjadi galat, silakan coba lagi.';

          $toast.show(errorMessage);
          $error.report(error);
        } finally {
          isSubmitting.value = false;
        }
      };

      return {
        isConfirmationModalOpen,
        isSubmitting,
        isLeavingAfterSubmit,
        showConfirmationModal,
        closeConfirmationModal,
        submitForm,
      };
    };

    const useLeaveConfirmation = () => {
      const pendingNavigation = ref(null);
      const isLeaveConfirmationModalOpen = ref(false);

      const showLeaveConfirmationModal = () => {
        isLeaveConfirmationModalOpen.value = true;
      };

      const closeLeaveConfirmationModal = () => {
        pendingNavigation.value = null;
        isLeaveConfirmationModalOpen.value = false;
      };

      const confirmLeave = () => {
        if (pendingNavigation.value) {
          $router.push(pendingNavigation.value);
        }
      };

      return {
        pendingNavigation,
        isLeaveConfirmationModalOpen,
        showLeaveConfirmationModal,
        closeLeaveConfirmationModal,
        confirmLeave,
      };
    };

    const {
      isConfirmationModalOpen,
      isSubmitting,
      isLeavingAfterSubmit,
      showConfirmationModal,
      closeConfirmationModal,
      submitForm,
    } = useSubmit();

    const {
      pendingNavigation,
      isLeaveConfirmationModalOpen,
      showLeaveConfirmationModal,
      closeLeaveConfirmationModal,
      confirmLeave,
    } = useLeaveConfirmation();

    return {
      isFetchingTenantContact,
      fetchTenantContact,
      tenantName,
      tenantPhoneNumber,
      uploadImage,
      deleteImage,
      isFormValid,

      isConfirmationModalOpen,
      isSubmitting,
      isLeavingAfterSubmit,
      showConfirmationModal,
      closeConfirmationModal,
      submitForm,

      pendingNavigation,
      isLeaveConfirmationModalOpen,
      showLeaveConfirmationModal,
      closeLeaveConfirmationModal,
      confirmLeave,

      // Phone validation related
      isPhoneNumberValidating,
      isPhoneNumberValid,
      phoneNumberValidationMessage,
      validatePhoneNumber,
      getPhoneNumberFieldMessage,
    };
  },
};
</script>
